import { type ForwardedRef, forwardRef, type InputHTMLAttributes } from "react";
import { twMerge } from "tailwind-merge";
import { InputRoot } from "./InputRoot";
import { generatePlaceholder } from "./utils/generatePlaceholder";
import { numberInputOnWheelPreventChange } from "./utils/numberInputWheel";

const getNumberInputPattern = ({
  fractionDigits,
  allowNegative,
}: {
  fractionDigits?: number;
  allowNegative?: boolean;
}) => {
  const sign = allowNegative ? "-?" : "";

  if (fractionDigits === undefined) {
    return new RegExp(`^${sign}\\d*(\\.\\d*)?$`);
  }

  if (fractionDigits < 0) {
    throw new Error("fractionDigits cannot be less than 0");
  }

  if (fractionDigits === 0) {
    return new RegExp(`^${sign}\\d*$`);
  }

  return new RegExp(`^${sign}\\d*(\\.\\d{0,${fractionDigits}})?$`);
};

type InputCurrencyProps = InputHTMLAttributes<HTMLInputElement> & {
  showPrefix?: boolean;
  fractionDigits?: number;
  allowNegative?: boolean;
};

export const InputCurrency = forwardRef(
  (
    {
      showPrefix = false,
      fractionDigits = 2,
      allowNegative = false,
      onChange,
      ...props
    }: InputCurrencyProps,
    ref: ForwardedRef<HTMLInputElement>,
  ) => {
    const inputPattern = getNumberInputPattern({
      fractionDigits,
      allowNegative,
    });

    const input = (
      <InputRoot
        ref={ref}
        onWheel={numberInputOnWheelPreventChange}
        {...props}
        type="number"
        step={props.step ?? "0.01"}
        className={twMerge("w-full", props.className)}
        placeholder={generatePlaceholder(props.placeholder)}
        onChange={(event) => {
          if (inputPattern.test(event.target.value)) {
            onChange?.(event);
          }
        }}
      />
    );

    if (!showPrefix) return input;

    return (
      <>
        <span className={twMerge(props.disabled && "text-zinc-300")}>£</span>
        {input}
      </>
    );
  },
);

InputCurrency.displayName = "InputCurrency";
